import * as React from "react";
import { Button, Empty, Form, Mentions, Radio, Select, Switch } from "antd";
import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { t } from "i18next";
import { trimRequired } from "src/common/validationRules/trimRequired";
import { ZOption } from "src/types/ZOption";
import { onError } from "src/common/onError";
import { RoleSelect } from "src/components/RoleSelect";
import { appStore } from "src/appStore";
import { getLangName } from "src/types/ZLanguageProps";
import { ZAttribute } from "src/types/ZAttribute";
import { InputNumberStd } from "src/components/InputNumberStd";
import { ZUid } from "src/types/zUid";
import { ZRolesGroup } from "../roles/roleTypes";
import { loadTemplatePlaceholders } from "../NotifyTemplates/loadTemplatePlaceholders";
import styles from "./DelayedNotify.module.less";
import {
  defaulDelayedNotify,
  fKey,
  root,
  typeOptions,
} from "./delayedNotifyUtils";
import { anyReceiverRule } from "./anyReceiverRule";

interface PropsDelayedNotify {
  stateId: ZUid;
  roles: ZRolesGroup[];
  userListAttrs: ZAttribute[];
  departmentListAttrs: ZAttribute[];
}

export const DelayedNotify: React.FC<PropsDelayedNotify> = ({
  stateId,
  roles: roleGroups,
  userListAttrs,
  departmentListAttrs,
}) => {
  const [placeholders, setPlaceholders] = React.useState<ZOption[]>([]);
  React.useEffect(() => {
    loadTemplatePlaceholders().then(setPlaceholders).catch(onError);
  }, []);

  return (
    <Form.List name={[root, stateId]}>
      {(fields, { add, remove }) => (
        <div className={styles.formListItem}>
          {fields.length === 0 && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
          {fields.map((field, index) => (
            <React.Fragment key={field.key}>
              <div className={styles.header}>
                <div className={styles.title}>Шаблон {index + 1}</div>
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => remove(field.name)}
                  title="Удалить шаблон"
                />
              </div>
              <Form.Item
                name={[field.name, fKey("active")]}
                label="Отправка уведомления"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                name={[field.name, fKey("days")]}
                label="Отправить через раб. дней"
                rules={[{ required: true }]}
              >
                <InputNumberStd
                  className={styles.daysInput}
                  precision={0}
                  min={0}
                />
              </Form.Item>
              <Form.Item
                name={[field.name, fKey("receiverAttrIds")]}
                label="Адресат (получатель)"
              >
                <Select
                  options={userListAttrs.map(({ id, name }) => ({
                    value: id,
                    label: name,
                  }))}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  placeholder="Выбрать"
                  mode="multiple"
                />
              </Form.Item>
              <Form.Item name={[field.name, fKey("roleIds")]} label="Роль">
                <RoleSelect roleGroups={roleGroups} />
              </Form.Item>
              <Form.Item
                name={[field.name, fKey("departmentAttrId")]}
                label="Подразделение"
                tooltip="Уведомление уйдёт всем пользователям подразделения, выбранного на карточке"
                dependencies={[
                  [root, stateId, field.name, fKey("roleIds")],
                  [root, stateId, field.name, fKey("receiverAttrIds")],
                ]}
                rules={[anyReceiverRule([root, stateId, field.name])]}
              >
                <Select
                  options={departmentListAttrs.map(({ id, name }) => ({
                    value: id,
                    label: name,
                  }))}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  placeholder="Выбрать"
                />
              </Form.Item>
              <Form.Item
                name={[field.name, fKey("messageType")]}
                label="Тип уведомления"
                rules={[{ required: true }]}
              >
                <Radio.Group
                  options={typeOptions()}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              {appStore.contentLanguages.map((lang) => (
                <Form.Item
                  key={lang.code}
                  name={[field.name, fKey("templateText"), lang.code]}
                  label={`Текст уведомления ${getLangName(lang)}`}
                  rules={[trimRequired()]}
                >
                  <Mentions
                    allowClear
                    rows={3}
                    prefix="%"
                    options={placeholders}
                  />
                </Form.Item>
              ))}
            </React.Fragment>
          ))}
          <Button
            icon={<PlusCircleOutlined />}
            onClick={() => add(defaulDelayedNotify)}
          >
            {t("Add")}
          </Button>
        </div>
      )}
    </Form.List>
  );
};
